import * as React from 'react';
import { Page } from "./base/page";
import { i18n } from "../config/i18n";
import { SubmitInputPrimary as SubmitInput } from '../ap/base/input';
import { TextInputField } from '../ap/fields/inputField';
import { FormState } from '../ap/fields/field';
import * as gls from '../ap/layout/gls';
import * as styles from '../ap/base/styles';
import * as text from '../ap/base/text';
import { Hr } from '../ap/base/hr';
import { Form } from '../ap/base/form';
import { route, navigateTo } from '../links';
import { sendPin } from '../webServices/smsService';
import * as formatters from '../utils/formatters';
import { DocumentSightingStepper, DocumentSightingStep } from './components/appStepper';
import { AddressInputs } from './components/address/addressInputs';
import TransactedAddressSelector from './components/address/TransactedAddressSelector';
import { UserNameInputs } from './components/userNameInputs';
import { FormIsland, MaxWidthIsland } from '../ap/pages/sections';
import { CheckboxField } from '../ap/fields/inputField';
import { Anchor } from '../ap/base/anchor';
import { observer } from 'mobx-react';
import { makeObservable, observable } from "mobx";
import { trackRegisterPage } from '../ap/omniture/omnitureService';

import { appState } from '../state/state';
import {SubmitButtonPrimary} from "../ap/base/input";
import { NotificationBar, Status } from './components/notificationBar';
const {voi} = appState;

export interface RegisterPageProps {
  state: RegisterPageState
}

export interface RegisterPageState {
  navigating?: boolean
}

class Messages
{
  static SuccessMessage: string = "Payment was successful. You'll receive an email when your VOI document is ready to view.";
  static FailureMessage: string = "Payment error! Looks like our website is experiencing some technical issues right now. Please try again later.";
}

export class RegisterPage extends React.Component<RegisterPageProps, RegisterPageState> {

  formState: FormState;

  public static defaultProps = {
    state: {
      navigating: false,
    } as RegisterPageState
  } as RegisterPageProps;

  constructor(props) {
    super(props);

    this.state = this.props.state;

    this.formState = new FormState(() => this.getFields());
  }

  getFields = () => {
    return [
      voi.firstName,
      voi.middleNames,
      voi.surname,

      voi.addressLookup,
      voi.addressLine1,
      voi.addressLine2,
      voi.suburb,
      voi.state,
      voi.postcode,

      voi.dob,
      voi.email,

      voi.phoneNumber,
      voi.propertyBeingTransacted,

      voi.privacyPolicyField,
    ]
  }

public register = () => {

    
    this.setState({ navigating: true });

    // Run validation and get the result immediately
    const validationResult = this.formState.validate();
    if (validationResult.hasError) {
        // Validation failed — errors are now set in FieldState
        // MobX observers will pick up changes and rerender the fields
        this.setState({ navigating: false });
        return;
    }

    const phone = formatters.cleanupPhoneNumbers(voi.phoneNumber.value);

    /** If phone number is still the same as what we verified, take them to VOI directly */
    if (phone === voi.verifiedPhoneNumber.value) {
        navigateTo(route.documentSighting(), true);
        return;
    }

    // Clear any pin code in previous attempts
    voi.pinCode.setValue('');

    sendPin({ phone })
        .then(() => {
            navigateTo(route.registerVerify(), true);
        })
        /** e.g. Too many SMSes */
        .catch(() => {
            navigateTo(route.registerVerify(), true);
        });
  };

  private cancel() {
    navigateTo(route.cancel(), true);
  }

  public useClientAddressAsTransactedAddress(useClientAddressAsTransacted:boolean) {
    voi.useClientAddressAsTransacted = useClientAddressAsTransacted;
    if (voi.useClientAddressAsTransacted) {
      voi.propertyBeingTransacted.setValue(voi.fullClientAddress);
    } else {
      voi.propertyBeingTransacted.setValue("");
    }
  }

  componentDidMount() {
    trackRegisterPage();
  }

  public render() {
    const sections = [
      <MaxWidthIsland>
        <DocumentSightingStepper current={DocumentSightingStep.Register} />
      </MaxWidthIsland>,
      <FormIsland>
        <NotificationBar type={Status.success} show={(sessionStorage.getItem('status') == Status.success)} message={Messages.SuccessMessage} />
        <NotificationBar type={Status.failure} show={(sessionStorage.getItem('status') == Status.failure)} message={Messages.FailureMessage} />

        <Form formId="registerForm" onSubmit={this.register}>
          <gls.ContentVerticalContentMargined margin={styles.spacing.verticalBetweenFields}>

            <text.Heading4 text="Identity Verifier to complete" />

            <text.Paragraph><span>Reminder: Prior to the collection of your client's personal information, you should ensure that the client ("Person Being Identified") is aware of the purpose for which their information is being collected and that they have consented to its collection.</span></text.Paragraph>

            <Hr />

            <UserNameInputs />

            <gls.ResponsiveGridParent margin={styles.spacing.horizontalBetweenFields}>
              <TextInputField id="dob" name="dob" label={i18n("dob")} star={true} fieldState={voi.dob} />
              <gls.FlexVertical>
                <TextInputField id="phoneNumber" name="phoneNumber" label={i18n("phoneNumber")} star={true} fieldState={voi.phoneNumber} />
                <gls.SmallVerticalSpace space={3} />
                <text.SmallText><span>Number will be verified on the following screen</span></text.SmallText>
              </gls.FlexVertical>
            </gls.ResponsiveGridParent>


            <AddressInputs />

            <TransactedAddressSelector useClientAddress={voi.useClientAddressAsTransacted} useClientAddressAsTransactedAddress={this.useClientAddressAsTransactedAddress}/>

            {
              !!voi.useClientAddressAsTransacted
                ? ''
                : <TextInputField id="propertyBeingTransacted" name="propertyBeingTransacted" label={i18n("propertyBeingTransacted")} star={true} fieldState={voi.propertyBeingTransacted} />
            }

            <Hr/>

            <text.Heading4 text="Person Being Identified to accept" />

            <gls.Content>
              <CheckboxField id="acceptConditionCheckbox" label={
                <text.ParagraphInline>
                  <span>I confirm that my client has provided their consent to the collection and use of their information and acknowledged the enclosed Privacy Notice:</span>
                </text.ParagraphInline>
              }
                  fieldState={voi.privacyPolicyField} />
            </gls.Content>
                <text.Paragraph>
                  <span>Your personal information is being captured to fulfil an in-person identity check by your conveyancer, lawyer or mortgagee. Without this information, they would be unable verify your identity in accordance with applicable standards. The personal information you disclose will be subject to the privacy policies or practices of your conveyancer, lawyer or mortgagee and may be shared with certain third parties contracted to assist them in the provision of their verification of identity services. Any personal information shared with and/or maintained by Australia Post as a result of use of the In-house VOI Solution will be handled in accordance with the Australia Post Group Privacy Policy which outlines how to access and/or correct your personal information or make a privacy related complaint. For more information please visit <Anchor href="https://auspost.com.au/privacy" target="_blank"><span>auspost.com.au/privacy</span></Anchor>.</span>
                </text.Paragraph>
            <gls.Content>

            </gls.Content>


            <gls.ContentHorizontal>

             <a href='' onClick={this.cancel}>Cancel</a>
             <gls.Flex />
              {
                <SubmitButtonPrimary id="submitButton" value={i18n("submitDetails")} disabled={this.formState.hasError} loading={!!this.state.navigating} />
              }

            </gls.ContentHorizontal>

          </gls.ContentVerticalContentMargined>
        </Form>
      </FormIsland>
    ];

    return (
      <Page loggedIn={true} sections={sections} />
    );
  }
}

export default observer(RegisterPage);
